import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { buttonClasses } from '@/components/generals/button'
import { getHoursSummary } from '@/lib/hours'
import { directionsUrl, navLinks, siteConfig } from '@/constants/site'

const hours = getHoursSummary()

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='bg-broth text-broth-foreground'>
            <div className='mx-auto max-w-6xl px-5 pb-10 pt-16 sm:px-8 lg:pt-20'>
                <div className='grid gap-12 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_0.8fr]'>
                    <div>
                        <Logo />

                        <p className='mt-5 max-w-xs text-sm leading-relaxed text-broth-foreground/70'>
                            Silky chicken porridge made from a broth we simmer overnight, finished fresh every morning.
                        </p>

                        <Link
                            href='/#order'
                            className={buttonClasses('light', 'sm', 'mt-6')}
                        >
                            Order ahead
                        </Link>
                    </div>

                    <div>
                        <h2 className='font-sans text-xs font-semibold uppercase tracking-[0.18em] text-broth-foreground/55'>
                            Visit
                        </h2>

                        <address className='mt-4 text-sm not-italic leading-relaxed'>
                            {siteConfig.address.street}
                            <br />
                            {siteConfig.address.city}, {siteConfig.address.region} {siteConfig.address.postalCode}
                        </address>

                        <div className='mt-4 flex flex-col items-start gap-2 text-sm'>
                            <a
                                href={directionsUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center gap-1 rounded-sm underline decoration-broth-foreground/30 underline-offset-4 outline-none transition-colors hover:decoration-broth-foreground focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                Get directions
                                <ArrowUpRight className='size-4' />
                            </a>

                            <a
                                href={siteConfig.phoneHref}
                                className='rounded-sm underline decoration-broth-foreground/30 underline-offset-4 outline-none transition-colors hover:decoration-broth-foreground focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                {siteConfig.phone}
                            </a>
                        </div>
                    </div>

                    <div>
                        <h2 className='font-sans text-xs font-semibold uppercase tracking-[0.18em] text-broth-foreground/55'>
                            Hours
                        </h2>

                        <dl className='mt-4 space-y-3 text-sm'>
                            {hours.map(group => (
                                <div key={group.label}>
                                    <dt className='text-broth-foreground/70'>
                                        {group.label}
                                    </dt>

                                    <dd className='font-medium'>
                                        {group.hours}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    <nav aria-label="Footer">
                        <h2 className='font-sans text-xs font-semibold uppercase tracking-[0.18em] text-broth-foreground/55'>
                            Explore
                        </h2>

                        <ul className='mt-4 space-y-2.5 text-sm'>
                            {navLinks.map(link => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        className='rounded-sm text-broth-foreground/85 outline-none transition-colors hover:text-broth-foreground focus-visible:ring-2 focus-visible:ring-ring'
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}

                            <li>
                                <Link
                                    href='/#order'
                                    className='rounded-sm text-broth-foreground/85 outline-none transition-colors hover:text-broth-foreground focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    Order ahead
                                </Link>
                            </li>
                        </ul>
                    </nav>
                </div>

                <div className='mt-14 flex flex-col gap-2 border-t border-broth-foreground/15 pt-6 text-xs text-broth-foreground/60 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        Simmered with care in {siteConfig.address.city}.
                    </p>
                </div>
            </div>
        </footer>
    )
}
